import React, { useState } from 'react';
import Modal from '../Modal/Modal';
import VisualInspectionTable from '../VisualInspectionTable/VisualInspectionTable';
import ApproveRejectButtons from '../ApproveRejectButtons';
import { INSPECTION_STATUS_VERDICT } from '../../utils/constants';
import { formatDate } from '../../utils/helper';
import { LATORERIA_ITEMS, PINTURA_ITEMS, VIDRIOS_ITEMS, isVisualInspectionComplete } from './visualInspectionItems';
import './VisualInspectionSection.css';

const { PENDING, IN_PROGRESS, APPROVED, REJECTED } = INSPECTION_STATUS_VERDICT;

const VisualInspectionSection = ({
    visualInspection = {},
    onStart,
    onApprove,
    onReject,
    onRevert,
    onChange,
    enableStartButton = false,
    enableApproveRejectButtons = false,
    enableRevertResultButton = false,
    currentUser
}) => {
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [showRevertModal, setShowRevertModal] = useState(false);

    const status = visualInspection.status || PENDING;
    const isComplete = isVisualInspectionComplete(visualInspection);
    const isDisabled = status === PENDING || !enableApproveRejectButtons;

    const getStateLabel = () => {
        if (status === APPROVED) return 'Aprobado';
        if (status === REJECTED) return 'Rechazado';
        if (status === IN_PROGRESS) return 'En Progreso';
        return 'Pendiente';
    };

    const handleApproveClick = () => {
        if (status === IN_PROGRESS && enableApproveRejectButtons) {
            if (!isComplete) {
                alert('Por favor, completa todas las ponderaciones (Bueno, Regular, Malo o N/A) en latonería, pintura y vidrios antes de aprobar.');
                return;
            }
            setConfirmAction(APPROVED);
            setShowConfirmModal(true);
        }
    };

    const handleRejectClick = () => {
        if (status === IN_PROGRESS && enableApproveRejectButtons) {
            if (!isComplete) {
                alert('Por favor, completa todas las ponderaciones antes de rechazar.');
                return;
            }
            setConfirmAction(REJECTED);
            setShowConfirmModal(true);
        }
    };

    const handleConfirmAction = () => {
        if (confirmAction === APPROVED) {
            onApprove?.();
        } else if (confirmAction === REJECTED) {
            onReject?.();
        }
        setShowConfirmModal(false);
        setConfirmAction(null);
    };

    const handleCancelModal = () => {
        setShowConfirmModal(false);
        setConfirmAction(null);
    };

    const handleCategoryChange = (category, itemId, value) => {
        if (!onChange) return;
        const next = {
            ...visualInspection,
            [category]: {
                ...(visualInspection[category] || {}),
                [itemId]: value
            }
        };
        onChange(next);
    };

    const handleCommentsChange = (e) => {
        if (onChange && status === IN_PROGRESS) {
            onChange({ ...visualInspection, comments: e.target.value });
        }
    };

    return (
        <div className="visual-inspection-section-card">
            <h2 className="inspection-areas-section-title">
                Inspección visual de latonería, pintura y vidrios
                {status === IN_PROGRESS && visualInspection.assignedTo && (
                    <span className="assignment-label">
                        Asignado a: {visualInspection.assignedTo.name}
                    </span>
                )}
                <span className={`status-badge ${status}`}>
                    {getStateLabel()}
                </span>
            </h2>

            {status === APPROVED || status === REJECTED ? (
                <>
                    <div className="completion-details">
                        <div className="completion-header">
                            <h3>Información de la sección completada</h3>
                            <div className="completion-meta">
                                <p><strong>Completado por:</strong> {visualInspection.finishedBy || '—'}</p>
                                <p><strong>Fecha:</strong> {visualInspection.finishedAt ? formatDate(visualInspection.finishedAt) : '—'}</p>
                            </div>
                        </div>
                    </div>
                    <div className="visual-inspection-tables visual-inspection-tables-readonly">
                        <VisualInspectionTable
                            title="Inspección visual de latonería"
                            items={LATORERIA_ITEMS}
                            values={visualInspection.latoneria || {}}
                            disabled={true}
                        />
                        <VisualInspectionTable
                            title="Inspección visual de pintura"
                            items={PINTURA_ITEMS}
                            values={visualInspection.pintura || {}}
                            disabled={true}
                        />
                        <VisualInspectionTable
                            title="Inspección visual de vidrios"
                            items={VIDRIOS_ITEMS}
                            values={visualInspection.vidrios || {}}
                            disabled={true}
                        />
                    </div>
                </>
            ) : (
                <div className="visual-inspection-tables-wrapper">
                    <div className={status === PENDING ? 'visual-inspection-section-disabled' : ''}>
                        {status === PENDING && !enableStartButton && (
                            <p className="visual-inspection-pending-text">Haz clic en Iniciar para desbloquear esta sección y asignarla a tu usuario.</p>
                        )}
                        <div className="visual-inspection-tables">
                            <VisualInspectionTable
                                title="Inspección visual de latonería"
                                items={LATORERIA_ITEMS}
                                values={visualInspection.latoneria || {}}
                                onChange={(itemId, value) => handleCategoryChange('latoneria', itemId, value)}
                                disabled={isDisabled}
                            />
                            <VisualInspectionTable
                                title="Inspección visual de pintura"
                                items={PINTURA_ITEMS}
                                values={visualInspection.pintura || {}}
                                onChange={(itemId, value) => handleCategoryChange('pintura', itemId, value)}
                                disabled={isDisabled}
                            />
                            <VisualInspectionTable
                                title="Inspección visual de vidrios"
                                items={VIDRIOS_ITEMS}
                                values={visualInspection.vidrios || {}}
                                onChange={(itemId, value) => handleCategoryChange('vidrios', itemId, value)}
                                disabled={isDisabled}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Footer con observaciones siempre visible (estándar Antecedentes/Dictamen) */}
            <div className="visual-inspection-footer">
                <div className="visual-inspection-comments-section">
                    <label htmlFor="visual-inspection-comments" className="visual-inspection-comments-label">
                        Observaciones y Comentarios sobre la parte interna del vehículo
                    </label>
                    <textarea
                        id="visual-inspection-comments"
                        className="visual-inspection-comments-textarea"
                        value={visualInspection.comments || ''}
                        onChange={handleCommentsChange}
                        placeholder="Escribe tus observaciones sobre la inspección visual aquí..."
                        rows={3}
                        disabled={status === PENDING || status === APPROVED || status === REJECTED}
                    />
                </div>
                {(status === PENDING || status === IN_PROGRESS) && (
                    <div className="verdict-actions">
                        {status === PENDING && enableStartButton && (
                            <button
                                type="button"
                                className="start-button"
                                onClick={onStart}
                            >
                                Iniciar
                            </button>
                        )}
                        {status === IN_PROGRESS && (
                            <ApproveRejectButtons
                                enableApproveButtons={enableApproveRejectButtons}
                                enableRejectButtons={enableApproveRejectButtons}
                                handleApproveClick={handleApproveClick}
                                handleRejectClick={handleRejectClick}
                                areRequiredFieldsComplete={() => isComplete}
                            />
                        )}
                    </div>
                )}
            </div>

            {enableRevertResultButton && (
                <div className="revert-result-button-container">
                    <button type="button" className="revert-result-button btn-primary" onClick={() => setShowRevertModal(true)}>
                        Revertir Resultado
                    </button>
                </div>
            )}

            <Modal
                isOpen={showRevertModal}
                onClose={() => setShowRevertModal(false)}
                title="Revertir Resultado"
            >
                <p>¿Estás seguro de querer revertir el resultado de esta sección?</p>
                <p className="modal-warning">Importante: Esta acción no se puede deshacer.</p>
                <div className="modal-actions">
                    <button type="button" className="modal-button cancel-button" onClick={() => setShowRevertModal(false)}>
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="revert-result-button btn-primary"
                        onClick={() => {
                            onRevert?.();
                            setShowRevertModal(false);
                        }}
                    >
                        Confirmar
                    </button>
                </div>
            </Modal>

            <Modal
                isOpen={showConfirmModal}
                onClose={handleCancelModal}
                title={confirmAction === APPROVED ? 'Confirmar Aprobación' : 'Confirmar Rechazo'}
            >
                <div className="modal-content">
                    <p>
                        ¿Estás seguro de que deseas {confirmAction === APPROVED ? 'aprobar' : 'rechazar'} la inspección visual (latonería, pintura y vidrios)?
                    </p>
                    <p className="modal-warning">Esta acción no se puede deshacer.</p>
                    <div className="modal-actions">
                        <button type="button" className="modal-button cancel-button" onClick={handleCancelModal}>
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className={`modal-button ${confirmAction === APPROVED ? 'verdict-approve-button' : 'verdict-reject-button'}`}
                            onClick={handleConfirmAction}
                        >
                            {confirmAction === APPROVED ? 'Aprobar' : 'Rechazar'}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default VisualInspectionSection;
export { isVisualInspectionComplete } from './visualInspectionItems';
